import BaseModel from './BaseModel';
import User from './User';
import Project from './Project';
import Change from './Change';
import moment from 'moment';
import type { Moment } from 'moment';
import _ from 'lodash';

export type Thread = {
    _id: string;
    comments: Comment[];
    startAt: Moment;
}

export type CommentReaction = {
    emoji: string;
    user: {
        _id: string;
        firstname?: string;
        lastname?: string;
        avatar?: string | null;
    };
    createdAt?: Moment | string | Date;
};

export const COMMENT_REACTION_EMOJIS = ['👍', '❤️', '🎉', '👀', '🤔', '👎'] as const;

/** Class representing a comment. */
class Comment extends BaseModel {
    _id: string;
    threadId?: string;
    createdAt: Moment;
    updatedAt?: Moment;
    author: User;
    project: Project;
    change: Change;
    message: string;
    /** Scrubbed original email HTML for sandboxed "view original" display. */
    originalMessage?: string | null;
    /** Forensic headers for inbound email-intake comments (not backfilled). */
    headers?: NonNullable<Change['intake']>['headers'] | null;
    kind?: 'comment' | 'reply' | 'forward';
    source?: string | null;
    rtTransactionId?: string | null;
    replyRecipients?: {
        to: string[];
        cc: string[];
    };
    reactions: CommentReaction[];
    commentIndex?: number;
    level?: number;

    constructor(properties: AnyObj) {
        super({});

        this._id      = properties._id;
        this.threadId = properties.threadId;
        this.author   = properties.author;
        this.project  = properties.project;
        this.change   = properties.change;
        this.message  = properties.message;
        this.originalMessage = properties.originalMessage || null;
        this.headers = properties.headers || null;
        this.kind = properties.kind || 'comment';
        this.source = properties.source || null;
        this.rtTransactionId = properties.rtTransactionId || null;
        this.replyRecipients = {
            to: Array.isArray(properties.replyRecipients?.to) ? properties.replyRecipients.to : [],
            cc: Array.isArray(properties.replyRecipients?.cc) ? properties.replyRecipients.cc : [],
        };
        this.reactions = Array.isArray(properties.reactions) ? properties.reactions : [];

        this.createdAt = moment(properties.createdAt);
        if (properties.updatedAt)
            this.updatedAt = moment(properties.createdAt);

        if (properties.author)
            this.author = new User(properties.author);

        if (properties.project)
            this.project = new Project(properties.project);

        if (properties.change)
            this.change = new Change(_.isObject(properties.change) ? properties.change : { _id: properties.change });

        if ('commentIndex' in properties)
            this.commentIndex = properties.commentIndex;

        if ('level' in properties)
            this.level = properties.level;
    }

    authorName() {
        return [this.author?.firstname, this.author?.lastname].filter(s => !!s).join(' ');
    }

    isReply() {
        return this.kind === 'reply';
    }

    isForward() {
        return this.kind === 'forward';
    }

    /** Outbound email/Signal message (reply or forward). */
    isOutbound() {
        return this.isReply() || this.isForward();
    }

    isRtImport() {
        return this.source === 'rt';
    }

    isEmailIntake() {
        return this.source === 'email_intake';
    }

    hasReacted(userId: string | undefined, emoji: string) {
        if (!userId)
            return false;
        return this.reactions.some(
            (reaction) => reaction.emoji === emoji && reaction.user?._id === userId
        );
    }

    reactionUsers(emoji: string) {
        return this.reactions
            .filter((reaction) => reaction.emoji === emoji)
            .map((reaction) => {
                const name = [reaction.user?.firstname, reaction.user?.lastname]
                    .filter(Boolean)
                    .join(' ');
                return name || reaction.user?._id || '';
            })
            .filter(Boolean);
    }

    /**
     * Display label for imported authors (strips required lastname placeholder "—").
     */
    externalAuthorLabel() {
        const parts = [this.author?.firstname, this.author?.lastname]
            .filter((s) => !!s && s !== '—');
        return parts.join(' ') || this.author?.email || '';
    }

    /**
     * Build a list of ordered threads
     */
    static toThreads(comments: Comment[]) : Thread[] {
        const threads: Record<string, Thread> = {};

        comments.forEach((c: Comment) => {
            if (!c.threadId)
                return;

            if (!(c.threadId in threads))
                threads[c.threadId] = {
                    _id: c.threadId,
                    comments: [],
                    startAt: c.createdAt
                };

            const thread = threads[c.threadId];
            c.level = thread.comments.length === 0 ? 0 : 1;
            thread.comments.push(c);
            thread.comments = thread.comments.sort((a: Comment, b: Comment) => a.createdAt.isBefore(b.createdAt) ? -1 : 1);
            if (c.createdAt.isBefore(thread.startAt))
                thread.startAt = c.createdAt;
        });

        return Object.values(threads).sort((a: Thread, b: Thread) => a.startAt.isBefore(b.startAt) ? 1 : -1);
    }
}

export default Comment;
